import { RoomDetailsElementType, StateroomDetailsProps } from '@msc-b2c/interfaces/types';
import styles from './stateroomdetails.module.scss';
import { replacePlaceholders, RootState } from '@msc-b2c/shared-utils';
import { Text } from '@msc-b2c/shared-components';
import PassengersInfo from './components/PassengersInfo';
import RoomDetails from './components/RoomDetails';
import IncludedWithBooking from './components/IncludedWithBooking';
import Excursions from './components/Excursions';
import Miscellaneous from './components/Miscellaneous';
import AlwaysIncluded from './components/AlwaysIncluded';
import DinningInfo from './components/DiningInfo';
import { useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { OnBoardServiceI } from '@msc-b2c/interfaces/interfaces';

export function StateroomDetails({
  stateroomCardTitle,
  passengerSectionTitle,
  passengerLabel,
  roomDetailsTitle,
  guaranteedCabinLabel,
  roomDetailsElements,
  excursionSectionTitle,
  excursionIcon,
  excursionIncludedLabel,
  includedSectionTitle,
  viewDetailsLabel,
  miscellaneousSectionTitle,
  miscellaneousIcon,
  miscellaneousIncludedLabel,
  alwaysIncludedTitle,
  alwaysIncludedElements,
  diningSectionTitle,
  diningLabel,
  stateroomIndex,
  stateroomCount,
  passengersData,
  cabinData,
  onBoardServicesData,
  diningOptionsData,
  insuranceData,
  hotelData,
  hotelSectionIcon,
  hotelSectionTitle,
  hotelLabels,
  flightsData,
  transfersData,
  openViewDetailsIncludedIcon,
  closeViewDetailsIncludedIcon,
  labelsMap,
  transfersSectionTitle,
  transfersSectionIcon,
  excursionData,
  miscellaneousItems,
}: Readonly<StateroomDetailsProps>) {
  // Card Variables
  const stateroomCardTitleEnchanced = replacePlaceholders(stateroomCardTitle, {
    cabinIndex: stateroomCount > 1 ? `${stateroomIndex + 1}` : '',
  });

  // Passenger Variables and utils
  const passengerNames =
    passengersData?.map((passenger) => {
      const { name, surname } = passenger;
      return `${name} ${surname}`;
    }) || [];

  // Room Details Variables and utils
  const shipInfo = useSelector((state: RootState) => state.bookingContentFragment.ship);

  const [roomDetails, setRoomDetails] = useState<RoomDetailsElementType[]>([]);

  useEffect(() => {
    const cabins = shipInfo
      ? [
          ...shipInfo.interiorCategoryCf,
          ...shipInfo.oceanViewCategoryCf,
          ...shipInfo.balconyCategoryCf,
          ...shipInfo.suiteCategoryCf,
          ...shipInfo.yachtClubCategoryCf,
        ]
      : [];

    const cabinType = cabinData?.categoryCode;
    const matchingCabin = cabins.find((cabin) => cabin.cabinCode === cabinType);

    const cabinTypeLabel = matchingCabin?.cabinNameShipPage || '';
    let cabinExperienceLabel = matchingCabin?.experienceCf?.experienceTitle || '';

    if (!cabinExperienceLabel) {
      const expService = onBoardServicesData?.find((service) => service.code?.startsWith('EXP'));
      if (expService?.code) {
        cabinExperienceLabel =
          labelsMap?.[expService.code]?.description || expService.description || '';
      }
    }

    const resolvedDetails = roomDetailsElements
      .map((element) => {
        let value;
        const isGuaranteedCabin = cabinData?.number === 'G00000';

        switch (element.elementType) {
          case 'Description':
            value = cabinData?.deckDescription || '';
            break;
          case 'Number':
            value = isGuaranteedCabin ? guaranteedCabinLabel : cabinData?.number || '';
            break;
          case 'Category':
            value = cabinTypeLabel;
            break;
          case 'Experience':
            value = cabinExperienceLabel;
            break;
          default:
            value = '';
        }

        return element.elementLabel && value ? { label: element.elementLabel, value } : null;
      })
      .filter((item): item is { label: string; value: string } => item !== null);

    setRoomDetails(resolvedDetails);
  }, [shipInfo, cabinData, roomDetailsElements, onBoardServicesData, labelsMap]);

  // Included with booking variables and utils
  const onboardServicesEnhanced =
    onBoardServicesData?.map((service: OnBoardServiceI) => {
      const matchedPassengers =
        service.passengers && service.passengers.length > 0
          ? service.passengers.map(({ id, number }) => {
              const fullData = passengersData?.find((p) => p.id === id);
              return {
                id: id ?? '',
                number: number ?? 0,
                name: fullData?.name || '',
                surname: fullData?.surname || '',
              };
            })
          : [];

      // Prefer label from labItem if available
      const enhancedDescription = labelsMap[service.code]?.description || service.description;
      const enhancedSpecialInstructions =
        labelsMap[service.code]?.specialInstructions || service.specialInstructions;

      return {
        id: service.id ?? '',
        description: enhancedDescription,
        specialInstructions: enhancedSpecialInstructions,
        passengers: matchedPassengers,
        code: service.code,
      };
    }) || [];

  const isDataReady =
    shipInfo &&
    cabinData &&
    passengersData &&
    passengersData?.length > 0 &&
    onBoardServicesData &&
    roomDetailsElements?.length > 0 &&
    alwaysIncludedElements?.length > 0 &&
    labelsMap;

  return (
    isDataReady && (
      <div className={styles.stateroomDetailsContainer}>
        <div className={styles.stateroomDetailsTitle}>
          <Text
            text={stateroomCardTitleEnchanced}
            tagHtml="h2"
          />
        </div>

        <div className={styles.bookingDetailsContainer}>
          <div className={styles.passengerDetailsContainer}>
            <div className={styles.passengerDetailsTitle}>
              <Text text={passengerSectionTitle} />
            </div>
            <div className={styles.passengerDetailsContent}>
              {passengerNames.map((passenger, index) => {
                const enhancedPassengerLabel = replacePlaceholders(passengerLabel, {
                  passengerIndex: passengerNames.length > 1 ? `${index + 1}` : '',
                });
                return (
                  <PassengersInfo
                    key={passenger}
                    passengerName={passenger}
                    passengerLabel={enhancedPassengerLabel}
                  />
                );
              })}
            </div>
          </div>
          {roomDetails.length > 0 && (
            <div className={styles.roomDetailsContainer}>
              <RoomDetails
                roomDetailsTitle={roomDetailsTitle}
                roomDetails={roomDetails}
              />
            </div>
          )}
        </div>

        <Excursions
          excursions={excursionData}
          passengers={passengersData}
          title={excursionSectionTitle}
          label={excursionIncludedLabel}
          icon={excursionIcon}
        />

        <IncludedWithBooking
          includedSectionTitle={includedSectionTitle}
          viewDetailsLabel={viewDetailsLabel}
          servicesWithPassengers={onboardServicesEnhanced}
          passengerLabel={passengerLabel}
          openViewDetailsIncludedIcon={openViewDetailsIncludedIcon}
          closeViewDetailsIncludedIcon={closeViewDetailsIncludedIcon}
          insuranceListInfo={insuranceData}
          hotelLabels={hotelLabels}
          hotelInfo={hotelData}
          hotelSectionIcon={hotelSectionIcon}
          hotelSectionTitle={hotelSectionTitle}
          flightsData={flightsData}
          transfersData={transfersData}
          transfersSectionTitle={transfersSectionTitle}
          transfersSectionIcon={transfersSectionIcon}
          labelsMap={labelsMap}
        />

        <Miscellaneous
          miscellaneousItems={miscellaneousItems}
          passengers={passengersData}
          title={miscellaneousSectionTitle}
          label={miscellaneousIncludedLabel}
          icon={miscellaneousIcon}
        />

        <AlwaysIncluded
          alwaysIncludedTitle={alwaysIncludedTitle}
          alwaysIncludedElements={alwaysIncludedElements}
        />
        <DinningInfo
          diningSectionTitle={diningSectionTitle}
          diningLabel={diningLabel}
          diningOptionsData={diningOptionsData}
        />
      </div>
    )
  );
}

export default StateroomDetails;
